// ===================================================================
// PA GRANTS — Module Layout
// Disaster selector bar drives all pages via shared store.
// ===================================================================

import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Button, Select, Group, Text } from '../../components/gab/GabComponents';
import {
  IconLayoutDashboard, IconClipboardList, IconBuildingCommunity, IconAlertTriangle,
  IconFileDescription, IconMap,
  IconClock, IconReceipt, IconSettings, IconShieldCheck, IconLeaf, IconCalendarEvent, IconMessageQuestion, IconListCheck, IconChartBar,
} from '@tabler/icons-react';
import { useFilteredNav } from '../../hooks/useFilteredNav';
import { useRoleStore } from '../../store/roleStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import type { NavItem } from '../../types/nav';
import ApplicantPortalLayout from './portal/ApplicantPortalLayout';
import ModuleNav from '../../components/layout/ModuleNav';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const APPLICANT_SLUGS = ['pa_applicant_mgr', 'pa_applicant_staff', 'pa_applicant_viewer'];

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard',       path: '/pagrants/pa',                      icon: IconLayoutDashboard, exact: true },
  { label: 'Disasters',       path: '/pagrants/pa/disasters',            icon: IconAlertTriangle },
  { label: 'Applicants',      path: '/pagrants/pa/applicants',           icon: IconBuildingCommunity },
  { label: 'Projects',        path: '/pagrants/pa/projects',             icon: IconClipboardList },
  { label: 'Map',             path: '/pagrants/pa/map',                  icon: IconMap },
  { label: 'Reimbursements',  path: '/pagrants/pa/reimbursements',       icon: IconReceipt },
  { label: 'Documents',       path: '/pagrants/pa/documents',            icon: IconFileDescription },
  { label: 'Insurance',       path: '/pagrants/pa/insurance',            icon: IconShieldCheck },
  { label: 'Compliance',      path: '/pagrants/pa/compliance',           icon: IconLeaf },
  { label: 'Timeline',        path: '/pagrants/pa/timeline',             icon: IconCalendarEvent },
  { label: 'Reports',         path: '/pagrants/pa/reports',              icon: IconChartBar },
  { label: 'RFIs',             path: '/pagrants/pa/rfis',                 icon: IconMessageQuestion,  minLevel: 'editor' },
  { label: 'Time Extensions', path: '/pagrants/pa/time-extensions',      icon: IconClock,            minLevel: 'editor' },
  { label: 'FEMA Forms',      path: '/pagrants/pa/forms',                icon: IconFileDescription as any,  minLevel: 'editor' },
  { label: 'Checklist Settings',    path: '/pagrants/pa/settings/checklist',        icon: IconSettings,   minLevel: 'admin' },
  { label: 'Milestone Templates',   path: '/pagrants/pa/settings/milestones',       icon: IconSettings,   minLevel: 'admin' },
  { label: 'Equipment Rates',       path: '/pagrants/pa/settings/equipment-rates',  icon: IconListCheck,  minLevel: 'admin' },
];

const NAV_GROUPS: { label: string; paths: string[] }[] = [
  { label: '', paths: ['/pagrants/pa', '/pagrants/pa/disasters', '/pagrants/pa/applicants', '/pagrants/pa/projects'] },
  { label: 'Records', paths: ['/pagrants/pa/map', '/pagrants/pa/reimbursements', '/pagrants/pa/documents', '/pagrants/pa/insurance', '/pagrants/pa/compliance', '/pagrants/pa/timeline', '/pagrants/pa/reports'] },
  { label: 'Admin', paths: ['/pagrants/pa/rfis', '/pagrants/pa/time-extensions', '/pagrants/pa/forms', '/pagrants/pa/settings/checklist', '/pagrants/pa/settings/milestones', '/pagrants/pa/settings/equipment-rates'] },
];

export default function PAGrantsLayout() {
  const effectiveRole = useRoleStore(s => s.demoRoleOverride ?? s.myRole);
  const slug = effectiveRole?.roleSlug ?? '';
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visibleItems = useFilteredNav('pagrants', NAV_ITEMS);

  // ── Disaster selector ──────────────────────────────────────────
  const disasters = usePAGrantsStore(s => s.disasters);
  const fetchDisasters = usePAGrantsStore(s => s.fetchDisasters);
  const activeDisasterId = usePAGrantsStore(s => s.activeDisasterId);
  const setActiveDisasterId = usePAGrantsStore(s => s.setActiveDisasterId);

  // Load disasters on layout mount
  useEffect(() => { fetchDisasters(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select on first load only — disaster is the PA module's own context, independent of incident
  useEffect(() => {
    if (activeDisasterId || disasters.length === 0) return;
    const first = disasters.find(d => d.status === 'active') ?? disasters[0];
    if (first) setActiveDisasterId(String(first.Rid));
  }, [disasters.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const disasterOptions = [
    { value: '', label: 'All Disasters' },
    ...disasters.map(d => ({
      value: String(d.Rid),
      label: [d.disasterNumber, d.disasterName].filter(Boolean).join(' — '),
    })),
  ];

  // ── Applicant portal fork ──────────────────────────────────────
  if (APPLICANT_SLUGS.includes(slug)) return <ApplicantPortalLayout />;

  const groups = NAV_GROUPS.map(g => ({
    ...g,
    items: g.paths.map(p => visibleItems.find(vi => vi.path === p)).filter(Boolean) as typeof visibleItems,
  })).filter(g => g.items.length > 0);

  const programsButton = (
    <Button variant="subtle" size="xs" color="#939598" onClick={() => navigate('/pagrants')} style={{ marginRight: 8 }}>
      &larr; Programs
    </Button>
  );

  const currentPage = NAV_ITEMS.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumbs
        items={[
          { label: 'Hub', to: '/' },
          { label: 'Disaster Grants', to: '/pagrants' },
          { label: 'Public Assistance', to: '/pagrants/pa' },
          ...(currentPage && currentPage.path !== '/pagrants/pa' ? [{ label: currentPage.label }] : []),
        ]}
      />

      {/* ── Disaster selector bar ── */}
      <Group gap="xs" align="center" style={{ padding: '8px 0', marginBottom: 4 }}>
        <Text size="xs" fw={600} c="dimmed" style={{ textTransform: 'uppercase', letterSpacing: '0.05em', minWidth: 56 }}>
          Disaster
        </Text>
        <Select
          size="xs"
          data={disasterOptions}
          value={activeDisasterId ?? ''}
          onChange={v => setActiveDisasterId(v || null)}
          style={{ width: 300 }}
        />
      </Group>

      <ModuleNav items={visibleItems} groups={groups} accentColor="#1E40AF" extra={programsButton} />
      <Outlet />
    </div>
  );
}
